import { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Chip, CircularProgress,
  Alert, Accordion, AccordionSummary, AccordionDetails, Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import SendIcon from '@mui/icons-material/Send';
import toast from 'react-hot-toast';
import { api } from '../../api/api';

export default function EmailTemplateSettings() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [edited, setEdited] = useState({});     // key → { subject, bodyHtml }
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => { fetchTemplates(); }, []);

  const fetchTemplates = async () => {
    try {
      const { data } = await api.get('/email-templates');
      setTemplates(data.templates);
    } catch (e) {
      toast.error('템플릿 로딩 실패');
    } finally { setLoading(false); }
  };

  const getEdited = (t) => edited[t.key] || { subject: t.subject, bodyHtml: t.bodyHtml };

  const handleChange = (key, field, value) => {
    setEdited((prev) => {
      const curr = prev[key] || { subject: templates.find((t) => t.key === key)?.subject, bodyHtml: templates.find((t) => t.key === key)?.bodyHtml };
      return { ...prev, [key]: { ...curr, [field]: value } };
    });
  };

  const handleSave = async (key) => {
    const data = getEdited(templates.find((t) => t.key === key));
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      await api.put(`/email-templates/${key}`, data);
      toast.success('템플릿이 저장되었습니다.');
      setEdited((prev) => { const n = { ...prev }; delete n[key]; return n; });
      fetchTemplates();
    } catch (e) {
      toast.error('저장 실패');
    } finally { setSaving((s) => ({ ...s, [key]: false })); }
  };

  const handleReset = async (key) => {
    if (!window.confirm('기본 템플릿으로 초기화하시겠습니까?')) return;
    try {
      await api.post(`/email-templates/${key}/reset`);
      toast.success('초기화 완료');
      setEdited((prev) => { const n = { ...prev }; delete n[key]; return n; });
      fetchTemplates();
    } catch (e) {
      toast.error('초기화 실패');
    }
  };

  const handleTest = async (key) => {
    if (!testEmail) { toast.error('테스트 수신 이메일을 입력하세요.'); return; }
    try {
      const { data } = await api.post(`/email-templates/${key}/test`, { testEmail });
      toast.success(data.message);
    } catch (e) {
      toast.error('테스트 발송 실패');
    }
  };

  if (loading) return <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={30} /></Box>;

  return (
    <Box>
      <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 1 }}>Email Templates</Typography>
      <Typography sx={{ fontSize: 13, color: '#888', mb: 2 }}>
        발송 이메일의 제목과 HTML 본문을 편집할 수 있습니다. {'{{변수}}'} 는 발송 시 실제 값으로 치환됩니다.
      </Typography>

      <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField size="small" label="테스트 수신 이메일" value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
          sx={{ width: 280, '& .MuiInputBase-root': { fontSize: 13 } }} />
      </Box>

      {templates.map((t) => {
        const ed = getEdited(t);
        const isModified = !!edited[t.key];
        return (
          <Accordion key={t.key} disableGutters sx={{ mb: 1, border: '1px solid #e5e7eb', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{t.name}</Typography>
                <Typography sx={{ fontSize: 12, color: '#888', flex: 1 }}>{t.description}</Typography>
                {isModified && <Chip label="수정됨" size="small" color="warning" sx={{ fontSize: 11, height: 20 }} />}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
                {(t.variables || []).map((v) => (
                  <Chip key={v} label={v} size="small" variant="outlined" sx={{ fontSize: 11, height: 22 }} />
                ))}
              </Box>

              <TextField fullWidth size="small" label="제목" value={ed.subject}
                onChange={(e) => handleChange(t.key, 'subject', e.target.value)}
                sx={{ mb: 1.5, '& .MuiInputBase-root': { fontSize: 13 } }} />

              <TextField fullWidth multiline minRows={8} maxRows={20} label="HTML 본문" value={ed.bodyHtml}
                onChange={(e) => handleChange(t.key, 'bodyHtml', e.target.value)}
                sx={{ mb: 1.5, '& .MuiInputBase-root': { fontSize: 12, fontFamily: 'monospace' } }} />

              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#888', mb: 0.5 }}>미리보기</Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, maxHeight: 300, overflow: 'auto' }}>
                <div dangerouslySetInnerHTML={{ __html: ed.bodyHtml }} />
              </Paper>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="contained" size="small" startIcon={<SaveIcon />}
                  onClick={() => handleSave(t.key)} disabled={saving[t.key]}>
                  {saving[t.key] ? <CircularProgress size={16} /> : '저장'}
                </Button>
                <Button variant="outlined" size="small" startIcon={<RestoreIcon />}
                  onClick={() => handleReset(t.key)} color="warning">
                  기본값 초기화
                </Button>
                <Button variant="outlined" size="small" startIcon={<SendIcon />}
                  onClick={() => handleTest(t.key)} disabled={!testEmail}>
                  테스트 발송
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}
